// ────────────────────────────────────────────────────────────────
// Page de connexion — JWT + 2FA TOTP
// ────────────────────────────────────────────────────────────────
import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import Chatbot from "../components/Chatbot";
import "../styles/design-tokens.css";

export default function Login({ onGoToRegister, onGoToForgotPassword } = {}) {
  const { login } = useAuth();
  const [step, setStep]           = useState("credentials"); // "credentials" | "twofa"
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [totp, setTotp]           = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password, step === "twofa" ? totp : undefined, rememberMe);
      if (res?.requiresTwoFa) {
        setStep("twofa");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="exp-public" style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, var(--ink) 0%, var(--surface) 55%, var(--surface-2) 100%)",
    }}>
      <Chatbot sourcePage="login" />
      <div style={{ width: "100%", maxWidth: 400, padding: "0 16px" }}>

        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <img src="/assets/logo-icon-execpro360.png" alt="ExecPro360" style={{ width: 40, height: 40, marginBottom: 12, objectFit: "contain" }} />
          <div style={{ fontFamily: "Manrope, sans-serif", fontSize: 22, fontWeight: 800, color: "var(--text)" }}>ExecPro360</div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
            Pilotage financier & opérationnel
          </div>
        </div>

        {/* Carte de connexion */}
        <div style={{
          background: "var(--surface)", border: "0.5px solid var(--hairline)",
          borderRadius: 16, padding: "28px 24px",
        }}>
          <h2 style={{ fontFamily: "Manrope, sans-serif", fontSize: 16, fontWeight: 700, color: "var(--text)", margin: "0 0 6px" }}>
            {step === "twofa" ? "🔐 Vérification 2FA" : "Connexion"}
          </h2>
          <p style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-muted)", margin: "0 0 20px" }}>
            {step === "twofa"
              ? "Entrez le code de votre application d'authentification."
              : "Accédez à votre espace financier sécurisé."}
          </p>

          {error && (
            <div style={{
              fontFamily: "Inter, sans-serif",
              padding: "10px 12px", background: "#3b1c1c", border: "0.5px solid #e24b4a",
              borderRadius: 8, fontSize: 12, color: "#fca5a5", marginBottom: 16,
            }}>
              ⚠️ {error}
            </div>
          )}

          <form onSubmit={handleLogin}>
            {step === "credentials" ? (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontFamily: "Manrope, sans-serif", fontSize: 12, color: "var(--text-muted)", display: "block", marginBottom: 6 }}>
                    Email professionnel
                  </label>
                  <input
                    type="email" required value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="vous@example.com" autoFocus
                    style={{
                      fontFamily: "Inter, sans-serif",
                      width: "100%", padding: "10px 12px", background: "var(--ink)",
                      border: "0.5px solid var(--hairline)", borderRadius: 8, color: "var(--text)",
                      fontSize: 13, outline: "none", boxSizing: "border-box",
                    }}
                  />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontFamily: "Manrope, sans-serif", fontSize: 12, color: "var(--text-muted)", display: "block", marginBottom: 6 }}>
                    Mot de passe
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showPassword ? "text" : "password"} required value={password} onChange={e => setPassword(e.target.value)}
                      placeholder="••••••••"
                      style={{
                        fontFamily: "Inter, sans-serif",
                        width: "100%", padding: "10px 40px 10px 12px", background: "var(--ink)",
                        border: "0.5px solid var(--hairline)", borderRadius: 8, color: "var(--text)",
                        fontSize: 13, outline: "none", boxSizing: "border-box",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                      style={{
                        position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                        background: "none", border: "none", cursor: "pointer",
                        fontSize: 15, padding: 4, lineHeight: 1, color: "var(--text-muted)",
                      }}
                    >
                      {showPassword ? "🙈" : "👁️"}
                    </button>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      id="remember" type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)}
                      style={{ width: 14, height: 14, accentColor: "var(--brand)", cursor: "pointer" }}
                    />
                    <label htmlFor="remember" style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-muted)", cursor: "pointer" }}>
                      Se souvenir de moi (30 jours)
                    </label>
                  </div>
                  {onGoToForgotPassword && (
                    <span
                      style={{ fontFamily: "Manrope, sans-serif", fontSize: 12, color: "var(--brand)", cursor: "pointer" }}
                      onClick={onGoToForgotPassword}
                    >Mot de passe oublié ?</span>
                  )}
                </div>
              </>
            ) : (
              <div style={{ marginBottom: 20 }}>
                <label style={{ fontFamily: "Manrope, sans-serif", fontSize: 12, color: "var(--text-muted)", display: "block", marginBottom: 6 }}>
                  Code 2FA (6 chiffres)
                </label>
                <input
                  type="text" required value={totp} onChange={e => setTotp(e.target.value)}
                  placeholder="123456" maxLength={6} autoFocus
                  style={{
                    fontFamily: "monospace",
                    width: "100%", padding: "14px 12px", background: "var(--ink)",
                    border: "0.5px solid var(--hairline)", borderRadius: 8, color: "var(--text)",
                    fontSize: 22, letterSpacing: 8, textAlign: "center",
                    outline: "none", boxSizing: "border-box",
                  }}
                />
                <div style={{ textAlign: "right", marginTop: 6 }}>
                  <span
                    style={{ fontFamily: "Manrope, sans-serif", fontSize: 11, color: "var(--brand)", cursor: "pointer" }}
                    onClick={() => setStep("credentials")}
                  >← Retour</span>
                </div>
              </div>
            )}

            <button
              type="submit" disabled={loading}
              style={{
                fontFamily: "Manrope, sans-serif",
                width: "100%", padding: "11px", background: loading ? "var(--brand-hover)" : "var(--brand)",
                border: "none", borderRadius: 8, color: "#fff",
                fontSize: 13, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                transition: "background 0.2s",
              }}
            >
              {loading ? "Connexion en cours..." : step === "twofa" ? "Valider le code" : "Se connecter"}
            </button>
          </form>

          {step === "credentials" && onGoToRegister && (
            <div style={{ fontFamily: "Inter, sans-serif", textAlign: "center", marginTop: 16, fontSize: 12, color: "var(--text-muted)" }}>
              Pas encore de compte ?{" "}
              <span
                style={{ fontFamily: "Manrope, sans-serif", color: "var(--brand)", cursor: "pointer", fontWeight: 600 }}
                onClick={onGoToRegister}
              >Essai gratuit</span>
            </div>
          )}

        </div>

        <div style={{ fontFamily: "Inter, sans-serif", textAlign: "center", marginTop: 20, fontSize: 11, color: "var(--text-faint)" }}>
          🔒 TLS 1.3 · RGPD · Chiffrement AES-256
        </div>
      </div>
    </div>
  );
}
